import { useState, useEffect } from 'react';
import {
  Search,
  MessageSquare,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  X,
  CheckCircle
} from 'lucide-react';
import adminService from '../../api/services/adminService';

const QUOTE_STATUSES = ['submitted', 'in_review', 'quoted', 'accepted', 'rejected', 'expired'];
const STATUS_COLORS = {
  submitted: 'bg-yellow-100 text-yellow-700',
  in_review: 'bg-blue-100 text-blue-700',
  quoted: 'bg-indigo-100 text-indigo-700',
  accepted: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700',
  expired: 'bg-gray-100 text-gray-500',
};

const CURRENCIES = ['USD', 'GBP', 'EUR', 'CAD', 'AED'];

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
};

const getStatusLabel = (status) => (status || '').replace(/_/g, ' ');
const getContactName = (quote) => quote.contactInfo?.name || quote.contactName || quote.name || '—';
const getContactEmail = (quote) => quote.contactInfo?.email || quote.email || '—';
const getOrganization = (quote) => quote.contactInfo?.organization || quote.organisation || quote.schoolName || '—';

const QuotesPage = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [pagination, setPagination] = useState({ page: 1, pages: 1, total: 0 });

  const [respondModal, setRespondModal] = useState(null);
  const [respondData, setRespondData] = useState({ status: 'quoted', quotedAmount: '', quoteCurrency: 'USD', quoteValidUntil: '', adminNotes: '' });
  const [respondError, setRespondError] = useState('');
  const [respondLoading, setRespondLoading] = useState(false);

  useEffect(() => {
    fetchQuotes();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, statusFilter]);

  const fetchQuotes = async () => {
    try {
      setLoading(true);
      const response = await adminService.getQuotes({
        page: pagination.page,
        limit: 15,
        status: statusFilter || undefined,
        search: searchTerm || undefined,
      });
      setQuotes(response.quotes || []);
      if (response.pagination) setPagination(response.pagination);
    } catch (err) {
      setError('Failed to load quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(p => ({ ...p, page: 1 }));
    fetchQuotes();
  };

  const openRespond = (quote) => {
    setRespondModal(quote);
    setRespondData({
      status: quote.status === 'submitted' ? 'quoted' : quote.status,
      quotedAmount: quote.quotedAmount || '',
      quoteCurrency: quote.quoteCurrency || 'USD',
      quoteValidUntil: quote.quoteValidUntil ? new Date(quote.quoteValidUntil).toISOString().split('T')[0] : '',
      adminNotes: quote.adminNotes || '',
    });
    setRespondError('');
  };

  const handleRespond = async (e) => {
    e.preventDefault();
    setRespondError('');
    setRespondLoading(true);
    try {
      await adminService.updateQuoteStatus(respondModal._id, {
        ...respondData,
        quotedAmount: respondData.quotedAmount !== '' ? parseFloat(respondData.quotedAmount) : undefined,
      });
      setRespondModal(null);
      fetchQuotes();
    } catch (err) {
      setRespondError(err.response?.data?.message || 'Failed to update quote');
    } finally {
      setRespondLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Quote Requests</h1>
          <p className="text-gray-500 mt-1">Manage bulk & institutional quote requests ({pagination.total} total)</p>
        </div>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-50 text-red-600 p-4 rounded-lg">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />{error}
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-xl shadow-sm p-4 border border-gray-100 flex flex-col sm:flex-row gap-3">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search by name or organisation..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
            />
          </div>
          <button type="submit" className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600 text-sm">Search</button>
        </form>
        <select
          value={statusFilter}
          onChange={(e) => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
          className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
        >
          <option value="">All Statuses</option>
          {QUOTE_STATUSES.map(s => <option key={s} value={s}>{s.charAt(0).toUpperCase() + s.slice(1)}</option>)}
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-100">
              <tr>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Contact</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Organisation</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Items</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Status</th>
                <th className="text-left px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Date</th>
                <th className="text-right px-6 py-3 text-xs font-semibold text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={6} className="py-12 text-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-500 mx-auto" /></td></tr>
              ) : quotes.length === 0 ? (
                <tr><td colSpan={6} className="py-12 text-center text-gray-400"><MessageSquare className="w-10 h-10 mx-auto mb-2 opacity-30" />No quote requests found</td></tr>
              ) : (
                quotes.map(quote => (
                  <tr key={quote._id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4">
                      <p className="font-medium text-gray-900 text-sm">{getContactName(quote)}</p>
                      <p className="text-xs text-gray-500">{getContactEmail(quote)}</p>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{getOrganization(quote)}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{quote.items?.length || quote.quantity || '—'}</td>
                    <td className="px-6 py-4">
                      <span className={`text-xs px-2 py-1 rounded-full font-medium ${STATUS_COLORS[quote.status] || 'bg-gray-100 text-gray-700'}`}>
                        {getStatusLabel(quote.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDate(quote.createdAt)}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => openRespond(quote)} className="inline-flex items-center gap-1 px-3 py-1 text-sm text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors">
                        <CheckCircle className="w-3.5 h-3.5" />Respond
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        {pagination.pages > 1 && (
          <div className="flex items-center justify-between px-6 py-4 border-t border-gray-100">
            <p className="text-sm text-gray-500">Page {pagination.page} of {pagination.pages}</p>
            <div className="flex gap-2">
              <button onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))} disabled={pagination.page === 1} className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"><ChevronLeft className="w-4 h-4" /></button>
              <button onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))} disabled={pagination.page === pagination.pages} className="p-2 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        )}
      </div>

      {/* Respond Modal */}
      {respondModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg">
            <div className="flex items-center justify-between p-6 border-b border-gray-100">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Respond to Quote</h2>
                <p className="text-sm text-gray-500 mt-0.5">{getContactName(respondModal)} — {getOrganization(respondModal)}</p>
              </div>
              <button onClick={() => setRespondModal(null)} className="p-2 hover:bg-gray-100 rounded-lg"><X className="w-5 h-5 text-gray-500" /></button>
            </div>
            <form onSubmit={handleRespond} className="p-6 space-y-4">
              {respondError && (
                <div className="flex items-center gap-2 bg-red-50 text-red-600 p-3 rounded-lg text-sm">
                  <AlertCircle className="w-4 h-4 flex-shrink-0" />{respondError}
                </div>
              )}

              {respondModal.message && (
                <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-700">
                  <p className="text-xs font-semibold text-gray-500 mb-1 uppercase tracking-wide">Message from customer</p>
                  <p>{respondModal.message}</p>
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
                <select value={respondData.status} onChange={e => setRespondData(d => ({ ...d, status: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                  {QUOTE_STATUSES.map(s => <option key={s} value={s}>{getStatusLabel(s).replace(/\b\w/g, c => c.toUpperCase())}</option>)}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Quoted Amount</label>
                  <input type="number" step="0.01" min="0" value={respondData.quotedAmount} onChange={e => setRespondData(d => ({ ...d, quotedAmount: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Currency</label>
                  <select value={respondData.quoteCurrency} onChange={e => setRespondData(d => ({ ...d, quoteCurrency: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300">
                    {CURRENCIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Quote Valid Until</label>
                <input type="date" value={respondData.quoteValidUntil} onChange={e => setRespondData(d => ({ ...d, quoteValidUntil: e.target.value }))} className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Admin Notes</label>
                <textarea rows={3} value={respondData.adminNotes} onChange={e => setRespondData(d => ({ ...d, adminNotes: e.target.value }))} placeholder="Internal notes or message to customer..." className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm resize-none focus:outline-none focus:ring-2 focus:ring-primary-300" />
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setRespondModal(null)} className="px-4 py-2 border border-gray-200 rounded-lg text-sm hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={respondLoading} className="px-4 py-2 bg-primary-500 text-white rounded-lg text-sm hover:bg-primary-600 disabled:opacity-60 flex items-center gap-2">
                  {respondLoading && <div className="animate-spin rounded-full h-3.5 w-3.5 border-b-2 border-white" />}
                  Save Response
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotesPage;
